import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Shield, Users, Settings, Plus, Edit, Trash2, Copy, 
  Search, RefreshCw, FileText, Calendar, DollarSign, 
  BarChart3, Pill, CheckSquare, Play, Pause, Zap,
  Bell, Mail, Globe, ChevronRight, Clock, AlertCircle,
  Check, X, Activity, Workflow
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RolePermissionBuilder() {
  const [activeTab, setActiveTab] = useState('roles');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Roles state
  const [roles, setRoles] = useState([]);
  const [roleStats, setRoleStats] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  
  // Permissions state
  const [permissions, setPermissions] = useState([]);
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [permissionsByCategory, setPermissionsByCategory] = useState({});
  
  // Workflows state
  const [workflows, setWorkflows] = useState([]);
  const [workflowStats, setWorkflowStats] = useState({});
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [workflowEvents, setWorkflowEvents] = useState([]);
  const [workflowActions, setWorkflowActions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes, workflowsRes, eventsRes, actionsRes] = await Promise.all([
        fetch(`${API_URL}/api/phase53/roles`),
        fetch(`${API_URL}/api/phase53/permissions`),
        fetch(`${API_URL}/api/phase53/workflows`),
        fetch(`${API_URL}/api/phase53/workflows/events`),
        fetch(`${API_URL}/api/phase53/workflows/actions`)
      ]);

      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setRoles(data.roles || []);
        setRoleStats(data.statistics || {});
      }

      if (permsRes.ok) {
        const data = await permsRes.json();
        setPermissions(data.permissions || []);
        setPermissionCategories(data.categories || []);
        setPermissionsByCategory(data.by_category || {});
      }

      if (workflowsRes.ok) {
        const data = await workflowsRes.json();
        setWorkflows(data.workflows || []);
        setWorkflowStats(data.statistics || {});
      }

      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setWorkflowEvents(data.events || []);
      }

      if (actionsRes.ok) {
        const data = await actionsRes.json();
        setWorkflowActions(data.action_types || []);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load admin tools data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (roleData) => {
    try {
      const response = await fetch(`${API_URL}/api/phase53/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(roleData)
      });

      if (response.ok) {
        toast.success('Role created successfully');
        fetchData();
        setRoleDialogOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create role');
      }
    } catch (error) {
      toast.error('Failed to create role');
    }
  };

  const handleUpdateRole = async (roleId, permissions) => {
    try {
      const response = await fetch(`${API_URL}/api/phase53/roles/${roleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions })
      });

      if (response.ok) {
        toast.success('Role permissions updated');
        fetchData();
      } else {
        toast.error('Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      const response = await fetch(`${API_URL}/api/phase53/roles/${roleId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Role deleted successfully');
        fetchData();
        setSelectedRole(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete role');
      }
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const handleCloneRole = async (roleId, newName) => {
    try {
      const response = await fetch(`${API_URL}/api/phase53/roles/${roleId}/clone?new_name=${encodeURIComponent(newName)}`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Role cloned successfully');
        fetchData();
      } else {
        toast.error('Failed to clone role');
      }
    } catch (error) {
      toast.error('Failed to clone role');
    }
  };

  const handleToggleWorkflow = async (workflowId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase53/workflows/${workflowId}/toggle`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      } else {
        toast.error('Failed to toggle workflow');
      }
    } catch (error) {
      toast.error('Failed to toggle workflow');
    }
  };

  const handleRunWorkflow = async (workflowId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase53/workflows/${workflowId}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      if (response.ok) {
        toast.success('Workflow executed successfully');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to run workflow');
      }
    } catch (error) {
      toast.error('Failed to run workflow');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      patients: Users,
      clinical: FileText,
      medications: Pill,
      scheduling: Calendar,
      billing: DollarSign,
      reports: BarChart3,
      admin: Settings
    };
    const Icon = icons[category] || Settings;
    return <Icon className="w-4 h-4" />;
  };

  const getActionIcon = (type) => {
    const icons = {
      notification: Bell,
      email: Mail,
      task: CheckSquare,
      update_field: Edit,
      webhook: Globe
    };
    const Icon = icons[type] || Zap;
    return <Icon className="w-4 h-4" />;
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredWorkflows = workflows.filter(workflow =>
    workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    workflow.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="admin-tools-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-teal-600 mb-4" />
          <p className="text-slate-600">Loading admin tools...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="admin-tools-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="admin-tools-title">
              <Shield className="w-7 h-7 text-teal-600" />
              Admin Tools
            </h1>
            <p className="text-slate-600 mt-1">Role permissions and workflow automation</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Roles</p>
                <p className="text-2xl font-bold text-slate-800" data-testid="stat-total-roles">{roleStats.total_roles || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-teal-600" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">{roleStats.system_roles || 0} system, {roleStats.custom_roles || 0} custom</p>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Users Assigned</p>
                <p className="text-2xl font-bold text-slate-800" data-testid="stat-users-assigned">{roleStats.total_users_assigned || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Across all roles</p>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Workflows</p>
                <p className="text-2xl font-bold text-slate-800" data-testid="stat-active-workflows">{workflowStats.active || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Workflow className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">of {workflowStats.total || 0} total</p>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Workflow Runs</p>
                <p className="text-2xl font-bold text-slate-800" data-testid="stat-workflow-runs">{workflowStats.total_executions || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Total executions</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="roles" data-testid="tab-roles">
              <Shield className="w-4 h-4 mr-2" />
              Roles & Permissions
            </TabsTrigger>
            <TabsTrigger value="workflows" data-testid="tab-workflows">
              <Workflow className="w-4 h-4 mr-2" />
              Workflow Automation
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
                data-testid="search-input"
              />
            </div>
            {activeTab === 'roles' && (
              <Button onClick={() => { setEditingRole(null); setRoleDialogOpen(true); }} data-testid="create-role-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            )}
            {activeTab === 'workflows' && (
              <Button onClick={() => setWorkflowDialogOpen(true)} data-testid="create-workflow-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Workflow
              </Button>
            )}
          </div>
        </div>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-4">
          <div className="grid grid-cols-3 gap-6">
            {/* Roles List */}
            <div className="col-span-1">
              <Card className="bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Roles</CardTitle>
                  <CardDescription>{filteredRoles.length} roles configured</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[500px] overflow-y-auto">
                    {filteredRoles.map((role) => (
                      <div
                        key={role.id}
                        className={`p-4 border-b cursor-pointer hover:bg-slate-50 transition-colors ${selectedRole?.id === role.id ? 'bg-teal-50 border-l-4 border-l-teal-500' : ''}`}
                        onClick={() => setSelectedRole(role)}
                        data-testid={`role-item-${role.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: role.color }}
                            />
                            <div>
                              <p className="font-medium text-slate-800">{role.name}</p>
                              <p className="text-xs text-slate-500">{role.user_count} users</p>
                            </div>
                          </div>
                          {role.is_system && (
                            <Badge variant="secondary" className="text-xs">System</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Role Details */}
            <div className="col-span-2">
              {selectedRole ? (
                <RoleDetailPanel
                  role={selectedRole}
                  permissions={permissions}
                  permissionCategories={permissionCategories}
                  permissionsByCategory={permissionsByCategory}
                  onUpdate={handleUpdateRole}
                  onDelete={handleDeleteRole}
                  onClone={handleCloneRole}
                  getCategoryIcon={getCategoryIcon}
                />
              ) : (
                <Card className="bg-white h-full flex items-center justify-center">
                  <div className="text-center text-slate-400">
                    <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Select a role to view and edit permissions</p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Workflows Tab */}
        <TabsContent value="workflows" className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {filteredWorkflows.map((workflow) => (
              <Card key={workflow.id} className="bg-white" data-testid={`workflow-item-${workflow.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${workflow.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
                        <Workflow className={`w-5 h-5 ${workflow.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{workflow.name}</h3>
                          <Badge variant={workflow.is_active ? 'default' : 'secondary'} className={workflow.is_active ? 'bg-green-500' : ''}>
                            {workflow.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{workflow.description}</p>
                        
                        {/* Trigger Info */}
                        <div className="flex items-center gap-4 mt-3">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Badge variant="outline" className="font-normal">
                              {workflow.trigger.type === 'event' && <Zap className="w-3 h-3 mr-1" />}
                              {workflow.trigger.type === 'schedule' && <Clock className="w-3 h-3 mr-1" />}
                              {workflow.trigger.type === 'event' ? workflow.trigger.event : `Schedule: ${workflow.trigger.schedule}`}
                            </Badge>
                          </div>
                          <span className="text-slate-300">→</span>
                          <div className="flex items-center gap-1">
                            {workflow.actions.map((action, idx) => (
                              <Badge key={idx} variant="outline" className="font-normal">
                                {getActionIcon(action.type)}
                                <span className="ml-1">{action.type}</span>
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                          <span>Runs: {workflow.run_count}</span>
                          {workflow.last_run && (
                            <span>Last: {new Date(workflow.last_run).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRunWorkflow(workflow.id)}
                        disabled={!workflow.is_active}
                        data-testid={`run-workflow-${workflow.id}`}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Run
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleWorkflow(workflow.id)}
                        data-testid={`toggle-workflow-${workflow.id}`}
                      >
                        {workflow.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedWorkflow(workflow); setWorkflowDialogOpen(true); }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Role Dialog */}
      <RoleDialog
        open={roleDialogOpen}
        onOpenChange={setRoleDialogOpen}
        role={editingRole}
        permissions={permissions}
        permissionsByCategory={permissionsByCategory}
        permissionCategories={permissionCategories}
        onSave={handleCreateRole}
        getCategoryIcon={getCategoryIcon}
      />

      {/* Create/Edit Workflow Dialog */}
      <WorkflowDialog
        open={workflowDialogOpen}
        onOpenChange={setWorkflowDialogOpen}
        workflow={selectedWorkflow}
        events={workflowEvents}
        actionTypes={workflowActions}
        onClose={() => { setWorkflowDialogOpen(false); setSelectedWorkflow(null); }}
      />
    </div>
  );
}

// Role Detail Panel Component
function RoleDetailPanel({ role, permissions, permissionCategories, permissionsByCategory, onUpdate, onDelete, onClone, getCategoryIcon }) {
  const [selectedPermissions, setSelectedPermissions] = useState(role.permissions || []);
  const [hasChanges, setHasChanges] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [showCloneDialog, setShowCloneDialog] = useState(false);

  useEffect(() => {
    setSelectedPermissions(role.permissions || []);
    setHasChanges(false);
  }, [role]);

  const handlePermissionToggle = (permCode) => {
    const newPermissions = selectedPermissions.includes(permCode)
      ? selectedPermissions.filter(p => p !== permCode)
      : [...selectedPermissions, permCode];
    setSelectedPermissions(newPermissions);
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(role.id, selectedPermissions);
    setHasChanges(false);
  };

  const handleClone = () => {
    if (cloneName.trim()) {
      onClone(role.id, cloneName.trim());
      setShowCloneDialog(false);
      setCloneName('');
    }
  };

  return (
    <Card className="bg-white" data-testid="role-detail-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: role.color }}
            />
            <div>
              <CardTitle className="text-lg">{role.name}</CardTitle>
              <CardDescription>{role.description}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Button onClick={handleSave} size="sm" data-testid="save-permissions-btn">
                <Check className="w-4 h-4 mr-1" />
                Save Changes
              </Button>
            )}
            <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Copy className="w-4 h-4 mr-1" />
                  Clone
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Clone Role</DialogTitle>
                  <DialogDescription>Enter a name for the cloned role</DialogDescription>
                </DialogHeader>
                <Input
                  placeholder="New role name"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCloneDialog(false)}>Cancel</Button>
                  <Button onClick={handleClone}>Clone Role</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {!role.is_system && (
              <Button variant="outline" size="sm" onClick={() => onDelete(role.id)} className="text-red-600 hover:text-red-700">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>{selectedPermissions.length} of {permissions.length} permissions enabled</span>
            <span>{role.user_count} users assigned</span>
          </div>

          {permissionCategories.map((category) => (
            <div key={category.id} className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                {getCategoryIcon(category.id)}
                <h4 className="font-medium text-slate-800">{category.name}</h4>
                <span className="text-xs text-slate-400">
                  ({(permissionsByCategory[category.id] || []).filter(p => selectedPermissions.includes(p.code)).length}/{(permissionsByCategory[category.id] || []).length})
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(permissionsByCategory[category.id] || []).map((perm) => (
                  <div key={perm.id} className="flex items-center gap-2">
                    <Checkbox
                      id={perm.code}
                      checked={selectedPermissions.includes(perm.code)}
                      onCheckedChange={() => handlePermissionToggle(perm.code)}
                      data-testid={`permission-${perm.code}`}
                    />
                    <label htmlFor={perm.code} className="text-sm text-slate-600 cursor-pointer">
                      {perm.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Role Dialog Component
function RoleDialog({ open, onOpenChange, role, permissions, permissionsByCategory, permissionCategories, onSave, getCategoryIcon }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#6B7280');
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const colors = ['#DC2626', '#7C3AED', '#2563EB', '#0891B2', '#059669', '#D97706', '#CA8A04', '#0D9488', '#EC4899', '#6B7280'];

  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description);
      setColor(role.color);
      setSelectedPermissions(role.permissions || []);
    } else {
      setName('');
      setDescription('');
      setColor('#6B7280');
      setSelectedPermissions([]);
    }
  }, [role, open]);

  const handlePermissionToggle = (permCode) => {
    setSelectedPermissions(prev =>
      prev.includes(permCode)
        ? prev.filter(p => p !== permCode)
        : [...prev, permCode]
    );
  };

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Role name is required');
      return;
    }
    onSave({
      name: name.trim(),
      description: description.trim(),
      color,
      permissions: selectedPermissions
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{role ? 'Edit Role' : 'Create New Role'}</DialogTitle>
          <DialogDescription>Configure role details and permissions</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Role Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Clinical Supervisor"
                data-testid="role-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Color</label>
              <div className="flex gap-2 mt-2">
                {colors.map((c) => (
                  <button
                    key={c}
                    className={`w-6 h-6 rounded-full border-2 ${color === c ? 'border-slate-800' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the role's responsibilities..."
              rows={2}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-3 block">Permissions</label>
            <div className="space-y-4 max-h-64 overflow-y-auto border rounded-lg p-4">
              {permissionCategories.map((category) => (
                <div key={category.id}>
                  <div className="flex items-center gap-2 mb-2">
                    {getCategoryIcon(category.id)}
                    <span className="font-medium text-sm">{category.name}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1 ml-6">
                    {(permissionsByCategory[category.id] || []).map((perm) => (
                      <div key={perm.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`new-${perm.code}`}
                          checked={selectedPermissions.includes(perm.code)}
                          onCheckedChange={() => handlePermissionToggle(perm.code)}
                        />
                        <label htmlFor={`new-${perm.code}`} className="text-xs text-slate-600 cursor-pointer">
                          {perm.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} data-testid="save-role-btn">
            {role ? 'Update Role' : 'Create Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Workflow Dialog Component
function WorkflowDialog({ open, onOpenChange, workflow, events, actionTypes, onClose }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState('event');
  const [triggerEvent, setTriggerEvent] = useState('');
  const [schedule, setSchedule] = useState('');

  useEffect(() => {
    if (workflow) {
      setName(workflow.name);
      setDescription(workflow.description);
      setTriggerType(workflow.trigger.type);
      setTriggerEvent(workflow.trigger.event || '');
      setSchedule(workflow.trigger.schedule || '');
    } else {
      setName('');
      setDescription('');
      setTriggerType('event');
      setTriggerEvent('');
      setSchedule('');
    }
  }, [workflow, open]);

  return (
    <Dialog open={open} onOpenChange={(val) => { onOpenChange(val); if (!val) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{workflow ? 'Edit Workflow' : 'Create New Workflow'}</DialogTitle>
          <DialogDescription>Configure workflow trigger and actions</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Workflow Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Patient Admission Alert"
              data-testid="workflow-name-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this workflow do?"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Trigger Type</label>
              <Select value={triggerType} onValueChange={setTriggerType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Event-based</SelectItem>
                  <SelectItem value="schedule">Scheduled</SelectItem>
                  <SelectItem value="manual">Manual Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {triggerType === 'event' && (
              <div>
                <label className="text-sm font-medium text-slate-700">Trigger Event</label>
                <Select value={triggerEvent} onValueChange={setTriggerEvent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((event) => (
                      <SelectItem key={event.code} value={event.code}>
                        {event.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {triggerType === 'schedule' && (
              <div>
                <label className="text-sm font-medium text-slate-700">Schedule (Cron)</label>
                <Input
                  value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  placeholder="0 9 * * 1 (Mon 9am)"
                />
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Available Actions</label>
            <div className="grid grid-cols-2 gap-2">
              {actionTypes.map((action) => (
                <div key={action.type} className="border rounded-lg p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    {action.type === 'notification' && <Bell className="w-4 h-4" />}
                    {action.type === 'email' && <Mail className="w-4 h-4" />}
                    {action.type === 'task' && <CheckSquare className="w-4 h-4" />}
                    {action.type === 'update_field' && <Edit className="w-4 h-4" />}
                    {action.type === 'webhook' && <Globe className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{action.name}</p>
                    <p className="text-xs text-slate-500">{action.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); onClose(); }}>Cancel</Button>
          <Button data-testid="save-workflow-btn">
            {workflow ? 'Update Workflow' : 'Create Workflow'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
